"use client";
import React, { useState, useEffect, useCallback } from "react";
import states from "../../../../public/states.json";
import countries from "../../../../public/countries.json";
import ContactAlert from "@/app/Components/Alerts/ContactAlert";
import {
	sendFormEvent,
	sendLeadGenerationEvent,
} from "@/app/_ga4/eventHandler";
import { validateForm } from "@/app/_lib_actions/validator";
import { TextInput } from "@/app/_components/FormComponents/FormInputs";
import { SelectInput } from "@/app/_components/FormComponents/FormInputs";
import { TextareaInput } from "@/app/_components/FormComponents/FormInputs";
import { AsYouType } from "libphonenumber-js";
import { addContactToLeadDatabase } from "@/app/_lib_actions/lead_handler";
import Link from "next/link";
import { MdEmail, MdPermPhoneMsg } from "react-icons/md";

const initialFormState = {
	origin: "FC_Website",
	company: "",
	address: "",
	address2: "",
	city: "",
	state: "",
	zip: "",
	country: "USA",
	website: "",
	fname: "",
	title: "",
	phone: "",
	mphone: "",
	email: "",
	numLocs: "",
	comment: "",
	current: 0,
	newsLetter: 0,
	ip: "",
};

const DemoRequestForm = () => {
	const [formData, setFormData] = useState(initialFormState);
	const [clientIp, setClientIp] = useState("");
	const [errors, setErrors] = useState({});
	const [showAlert, setShowAlert] = useState(false);
	const [alertStatus, setAlertStatus] = useState("");
	const [alertMessage, setAlertMessage] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [formStarted, setFormStarted] = useState(false);
	const sortedCountries = [
		{ id: "USA", name: "United States", abbreviation: "USA" },
		...countries
			.filter((c) => c.id !== "USA")
			.sort((a, b) => a.name.localeCompare(b.name)),
	];

	// Fetch client IP
	useEffect(() => {
		const fetchClientIp = async () => {
			try {
				const response = await fetch(
					"https://api64.ipify.org?format=json"
				);
				const data = await response.json();
				setClientIp(data.ip);
			} catch (error) {
				console.error("Error fetching client IP:", error);
			}
		};
		fetchClientIp();
	}, []);


	const handleChange = useCallback(
		(e) => {
			const { name, value } = e.target;

			setFormData((prevData) => {
				const updatedData = { ...prevData, [name]: value };

				// Clear phone numbers when the country changes
				if (name === "country") {
					updatedData.phone = ""; // Clear phone
					updatedData.mphone = ""; // Clear mobile phone
				}

				// Format phone or mobile phone dynamically as user types
				if (name === "phone" || name === "mphone") {
					const formatter = new AsYouType(
						prevData.country === "USA" || "Select Country" ? "US" : prevData.country
					);
					const cleanInput = value.replace(/[^\d+]/g, ""); // Clean current input
					updatedData[name] = formatter.input(cleanInput);
				}


				// Track form start
				if (!formStarted) {
					setFormStarted(true);
					sendFormEvent("DEMO_REQUEST", "START");
				}

				return updatedData;
			});
		},
		[formStarted]
	);
	const closeAlert = () => {
		setShowAlert(false);
	};



	const requestDemo = async (data) => {
		try {
			const response = await fetch("/api/contact", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify(data),
			});
			const result = await response.json();
			if (result.success) {
				setAlertStatus("Success");
				setShowAlert(true);
				setAlertMessage(result.data);
				setFormData(initialFormState);
				setIsSubmitting(false);
				return result;
			} else {
				setAlertStatus("Failed");
				setAlertMessage(result.data);
				setShowAlert(true);
				setIsSubmitting(false);
			}
		} catch (error) {
			setAlertStatus("Failed");
			setAlertMessage(error);
			setShowAlert(true);
			setIsSubmitting(false);
			return { success: false };
		}
	};

	const handleDevSubmit = async (e) => {
		e.preventDefault();
		if (isSubmitting) return;
		setIsSubmitting(true);

		const validationErrors = validateForm(formData);
		if (Object.keys(validationErrors).length > 0) {
			setErrors(validationErrors);
			setIsSubmitting(false);
			return;
		}
		await addContactToLeadDatabase(formData, "request_demo_form");
		const demoResponse = await requestDemo(formData);
		if (demoResponse?.success) {
			setFormData(initialFormState);
			setFormStarted(false);
		} else {
			console.error("Error submitting request");
		}
		setIsSubmitting(false);
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		if (isSubmitting) return;
		setIsSubmitting(true);
		sendFormEvent("DEMO_REQUEST", "SUBMIT");
		const validationErrors = validateForm(formData);
		if (Object.keys(validationErrors).length > 0) {
			setErrors(validationErrors);
			setIsSubmitting(false);
			return;
		}
		await addContactToLeadDatabase(formData, "request_demo_form");
		const demoResponse = await requestDemo(formData);
		if (demoResponse?.success) {
			setFormData(initialFormState);
			sendLeadGenerationEvent("DEMO_REQUEST");
			setFormStarted(false);
		} else {
			sendFormEvent("DEMO_REQUEST", "INVALID/ERROR");
		}
		setIsSubmitting(false);
	};

	const handleAutoFill = (e) => {
		e.preventDefault();
		setFormData({
			origin: "FC_Website",
			company: "DELETE ME RPS Development Team",
			address: "123 Main St",
			address2: "Suite 42",
			city: "Sample City",
			state: "Pennsylvania",
			zip: "12345",
			country: "USA",
			website: "https://example.com",
			fname: "John Doe",
			title: "Manager",
			phone: "8145380204",
			mphone: "8145380204",
			email: "lauer.chris1@gmail.com",
			numLocs: "5",
			comment: "This is a test comment.",
			current: 0,
			newsLetter: 0,
			ip: clientIp,
		});
	};


	return (
		<div className="machine-demo-request">

			{showAlert && (
				<ContactAlert
					showAlert={showAlert}
					alertMessage={alertMessage}
					alertStatus={alertStatus}
					closeAlert={closeAlert}

				/>
			)}
			<div className="left-column">
				<div>
					<h2 className="title">Request a Free Demo</h2>
					<p>
						Request a free demo of our products and see how they can help your
						business.
					</p>
				</div>
				<div>
					<h3>Rather call?</h3>
					<p>
						One of our customer service representatives will be happy to assist you with any questions you may have.
					</p>
					<ul>
						<li>
							<MdPermPhoneMsg />
							<Link href="tel:1 262-681-3583">
								1 262-681-3583
							</Link>
						</li>
					</ul>
				</div>
				<div>
					<h3>Rather Email?</h3>
					<p>
						Whether you have a question about features, trials, pricing, need a demo, or anything else, our team is ready to answer all your questions.
					</p>
					<ul>
						<li>
							<MdEmail />
							<Link href="email:customerservice@rpscorporation.com">
								customerservice@rpscorporation.com
							</Link>
						</li>
					</ul>
				</div>



			</div>
			<div className="right-column">
				<form
					className='register-form'
					onSubmit={
						process.env.NODE_ENV === "development"
							? handleDevSubmit
							: handleSubmit
					}>
					{/* {process.env.NODE_ENV === "development" && (
						<button className='dev-button' onClick={handleAutoFill}>
							Log Form Data
						</button>
					)} */}
					<div className='form-section'>
						<h3>Company Information</h3>
						<TextInput
							label={"Company Name"}
							name={"company"}
							value={formData.company}
							error={errors.company}
							onChange={handleChange}
							placeholder={"Company Name"}
						/>
						<div className='form-two-column'>
							<TextInput
								label={"Street Address"}
								name={"address"}
								value={formData.address}
								error={errors.address}
								onChange={handleChange}
								placeholder={"123 Avenue"}
							/>
							<TextInput
								label={"Suite"}
								name={"address2"}
								value={formData.address2}
								error={errors.address2}
								onChange={handleChange}
								placeholder={"Suite 42"}
							/>
						</div>
						<div className='form-three-column'>
							<TextInput
								label={"City"}
								name={"city"}
								value={formData.city}
								error={errors.city}
								onChange={handleChange}
								placeholder={"City"}
							/>
							<SelectInput
								label={"State"}
								name={"state"}
								value={formData.state}
								options={states}
								error={errors.state}
								onChange={handleChange}
								placeholder={"No State Selected"}
							/>
							<TextInput
								label={"Zip*"}
								name={"zip"}
								value={formData.zip}
								error={errors.zip}
								onChange={handleChange}
								placeholder={"12345"}
							/>
						</div>
						<div className='form-two-column'>
							<SelectInput
								label={"Country"}
								name={"country"}
								value={formData.country}
								options={sortedCountries}
								error={errors.country}
								onChange={handleChange}
							/>
							<TextInput
								label={"Website"}
								name={"website"}
								value={formData.website}
								error={errors.website}
								onChange={handleChange}
								placeholder={"Website URL"}
							/>
						</div>
					</div>

					<div className='form-section'>
						<h3>Primary Contact Info</h3>
						<TextInput
							label={"Full Name*"}
							name={"fname"}
							value={formData.fname}
							error={errors.fname}
							onChange={handleChange}
							placeholder={"John Smith"}
						/>

						<div className='form-two-column'>
							<TextInput
								label={"Job Position"}
								name={"title"}
								value={formData.title}
								error={errors.title}
								onChange={handleChange}
								placeholder={"Job Position"}
							/>

							<TextInput
								label={"Number of Locations"}
								name={"numLocs"}
								value={formData.numLocs}
								error={errors.numLocs}
								onChange={handleChange}
								placeholder={"1"}
							/>
						</div>
						<div className='form-two-column'>
							<TextInput
								label={"Office Number*"}
								name={"phone"}
								value={formData.phone}
								error={errors.phone}
								onChange={handleChange}
								placeholder={"123-456-7890"}
							/>
							<TextInput
								label={"Mobile Number*"}
								name={"mphone"}
								value={formData.mphone}
								error={errors.mphone}
								onChange={handleChange}
								placeholder={"123-456-7890"}
							/>
						</div>
						<div className='form-two-column'>
							<TextInput
								label={"Email*"}
								name={"email"}
								value={formData.email}
								error={errors.email}
								onChange={handleChange}
								placeholder={"abcd@gmail.com"}
							/>

						</div>
					</div>

					<TextareaInput
						label='Comments'
						name='comment'
						value={formData.comment}
						error={errors.comment}
						onChange={handleChange}
						placeholder='Comments'
					/>
					<div className='button-wrapper'>
						<button
							className='form-submit'
							type='submit'
							disabled={isSubmitting}>
							{isSubmitting ? "Submitting..." : "Submit"}
						</button>
					</div>
				</form>
			</div>


		</div>
	);
};

export default DemoRequestForm;
